import { TuiDocument } from '../core/Document';
import { Attr, ColorMode, TRANSPARENT } from '../core/types';
import { analyzeChar } from '../unicode/width';
import { describeColor } from '../ansi/Palette';

/**
 * Terminal profiles and the compatibility checker.
 *
 * The checker answers one question: "if I open this design in that terminal,
 * what breaks?" It reports concrete cell counts and example positions rather
 * than generic advice, so a warning can be acted on.
 */

export interface TerminalProfile {
  id: string;
  name: string;
  width: number;
  height: number;
  colors: ColorMode;
  supportsBlink: boolean;
  supportsItalic: boolean;
  supportsUnicode: boolean;
  /** Renders box-drawing and block elements reliably. */
  supportsBoxDrawing: boolean;
  note: string;
}

export const TERMINAL_PROFILES: readonly TerminalProfile[] = [
  {
    id: 'xterm256',
    name: 'XTerm 256',
    width: 80,
    height: 24,
    colors: 'ansi256',
    supportsBlink: true,
    supportsItalic: false,
    supportsUnicode: true,
    supportsBoxDrawing: true,
    note: 'Classic baseline. 256 colours, blink honoured, italics usually not.',
  },
  {
    id: 'putty',
    name: 'PuTTY',
    width: 120,
    height: 30,
    colors: 'ansi256',
    supportsBlink: true,
    supportsItalic: false,
    supportsUnicode: true,
    supportsBoxDrawing: true,
    note: 'TrueColor is ignored on older builds; verify the font covers box drawing.',
  },
  {
    id: 'linux-console',
    name: 'Linux console',
    width: 80,
    height: 25,
    colors: 'ansi16',
    supportsBlink: true,
    supportsItalic: false,
    supportsUnicode: false,
    supportsBoxDrawing: false,
    note: 'Only 16 colours and a limited glyph set — the strictest target.',
  },
  {
    id: 'windows-terminal',
    name: 'Windows Terminal',
    width: 120,
    height: 30,
    colors: 'truecolor',
    supportsBlink: false,
    supportsItalic: true,
    supportsUnicode: true,
    supportsBoxDrawing: true,
    note: 'Full TrueColor and Unicode. Blink is not rendered.',
  },
  {
    id: 'modern-ssh',
    name: 'Modern SSH (TrueColor)',
    width: 160,
    height: 50,
    colors: 'truecolor',
    supportsBlink: false,
    supportsItalic: true,
    supportsUnicode: true,
    supportsBoxDrawing: true,
    note: 'kitty / WezTerm / iTerm2 class terminal.',
  },
  {
    id: 'irc',
    name: 'IRC client',
    width: 80,
    height: 25,
    colors: 'ansi16',
    supportsBlink: false,
    supportsItalic: false,
    supportsUnicode: true,
    supportsBoxDrawing: true,
    note: 'Colour support varies wildly between clients; 16 colours is the safe floor.',
  },
];

export type IssueSeverity = 'error' | 'warning' | 'info';

export interface CompatibilityIssue {
  severity: IssueSeverity;
  title: string;
  detail: string;
  /** How many cells are affected, when that is meaningful. */
  count?: number;
  example?: { x: number; y: number };
}

export function checkCompatibility(doc: TuiDocument, profile: TerminalProfile): CompatibilityIssue[] {
  const issues: CompatibilityIssue[] = [];
  const grid = doc.composite();

  if (doc.width > profile.width || doc.height > profile.height) {
    issues.push({
      severity: 'error',
      title: `Larger than ${profile.name} (${profile.width}×${profile.height})`,
      detail: `The document is ${doc.width}×${doc.height}. Content outside the terminal will wrap or be cut off.`,
    });
  }

  let shifted = 0;
  let shiftedExample: { x: number; y: number } | undefined;
  let blink = 0;
  let italic = 0;
  const wideChars = new Map<string, { count: number; message: string; x: number; y: number }>();
  let nonAscii = 0;
  let boxChars = 0;

  grid.forEachCell((cell, x, y) => {
    if (profile.colors !== 'truecolor') {
      for (const color of [cell.fg, cell.bg]) {
        if (color === TRANSPARENT) continue;
        const info = describeColor(color);
        const exact = profile.colors === 'ansi16' ? info.exactIn16 : info.exactIn256;
        if (!exact) {
          shifted += 1;
          if (!shiftedExample) shiftedExample = { x, y };
          break;
        }
      }
    }
    if ((cell.attrs & Attr.BLINK) !== 0 && !profile.supportsBlink) blink += 1;
    if ((cell.attrs & Attr.ITALIC) !== 0 && !profile.supportsItalic) italic += 1;

    if (cell.char !== '' && cell.char !== ' ') {
      const cp = cell.char.codePointAt(0) ?? 0;
      if (cp > 127) nonAscii += 1;
      if (cp >= 0x2500 && cp <= 0x259f) boxChars += 1;
      const warning = analyzeChar(cell.char);
      if (warning && (warning.risk === 'wide' || warning.risk === 'ambiguous')) {
        const entry = wideChars.get(cell.char);
        if (entry) entry.count += 1;
        else wideChars.set(cell.char, { count: 1, message: warning.message, x, y });
      }
    }
  });

  if (shifted > 0) {
    issues.push({
      severity: 'warning',
      title: `${profile.colors === 'ansi16' ? '16' : '256'}-colour approximation`,
      detail: `${shifted} cells use colours that shift when quantised for ${profile.name}. Preview in that mode to check the result.`,
      count: shifted,
      example: shiftedExample,
    });
  }
  if (blink > 0) {
    issues.push({
      severity: 'warning',
      title: 'Blink not supported',
      detail: `${profile.name} does not render blink; ${blink} cells will appear static.`,
      count: blink,
    });
  }
  if (italic > 0) {
    issues.push({
      severity: 'info',
      title: 'Italic not supported',
      detail: `${italic} cells use italic, which ${profile.name} ignores or renders as inverse.`,
      count: italic,
    });
  }
  for (const [char, info] of wideChars) {
    issues.push({
      severity: 'warning',
      title: `Character "${char}" may occupy 2 cells`,
      detail: `${info.message} Used ${info.count} time(s); the layout will shift right of it if the terminal disagrees.`,
      count: info.count,
      example: { x: info.x, y: info.y },
    });
  }
  if (nonAscii > 0 && !profile.supportsUnicode) {
    issues.push({
      severity: 'error',
      title: 'Unicode required',
      detail: `${nonAscii} cells use non-ASCII characters, which ${profile.name} may not render.`,
      count: nonAscii,
    });
  }
  if (boxChars > 0 && !profile.supportsBoxDrawing) {
    issues.push({
      severity: 'warning',
      title: 'Box drawing may not render',
      detail: `${boxChars} box-drawing or block cells; ${profile.name} needs a font that covers U+2500–U+259F.`,
      count: boxChars,
    });
  }

  if (issues.length === 0) {
    issues.push({
      severity: 'info',
      title: 'No compatibility issues found',
      detail: `This design should render as designed on ${profile.name}.`,
    });
  }
  return issues;
}
